/**
 * The approval queue.
 *
 * ═══════════════════════════════════════════════════════════════════
 * APPROVING IS A DECISION, AND IT IS RECORDED AS ONE.
 *
 * Every decision made here writes an approval step with the approver, the
 * moment, the comment and the authority they held when they made it. A
 * rejection requires a reason, because "no" without a reason is not an answer
 * the requester can act on.
 *
 * The server decides whether the decision is allowed. It refuses self-approval,
 * it refuses an approver whose limit is below the value, and it refuses a stage
 * that is not theirs — whatever this screen chooses to render.
 *
 * And it tells the approver what their decision did. A queue that simply loses
 * a row is a screen that answered the wrong question: the approver wants to
 * know whether the thing is now authorised or has passed to somebody else, and
 * the server returns exactly that, so it is shown.
 * ═══════════════════════════════════════════════════════════════════
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useAsync, useMutation } from '../lib/useAsync';
import { lower, useTerms, type Terminology } from '../lib/tenant';
import { date, dateTime, humanise, money, todayIso } from '../lib/format';
import { Alert, AsyncPanel, Badge, Card, DataTable, EmptyState, ErrorNotice, Modal, Money, PageHeader, TextArea } from '../components/ui';

interface PendingApproval {
  requestId: string;
  stepId: string;
  subjectType: string;
  subjectId: string;
  subjectReference: string;
  subjectSummary: string;
  valueMinor: number;
  currency: string;
  stageName: string;
  stageNo: number;
  originatorName: string;
  costCentreCode: string | null;
  startedAt: string;
  dueAt: string | null;
  isOverdue: boolean;
  onBehalfOfName: string | null;
}

/** What the server answers with once a decision is recorded. */
interface DecisionResponse {
  requestStatus: 'in_progress' | 'approved' | 'rejected';
  completedStageNo: number;
  nextStageNo: number | null;
  nextAssigneeName: string | null;
  isFinal: boolean;
  /** Present only when the document that completed approval was a requisition. */
  requisitionNo?: string;
  fulfilmentMethod?: string;
}

interface Outcome {
  decision: 'approve' | 'reject';
  reference: string;
  subjectType: string;
  subjectId: string;
  originatorName: string;
  result: DecisionResponse;
}

/**
 * Where a document of each kind can actually be looked at.
 *
 * `detail` is set only for the kinds that genuinely have a `/:id` route. The
 * previous map appended the id for every type, so a sales invoice request
 * produced `/sales/<uuid>`, which matches no route and falls through to the
 * catch-all redirect: the approver clicked a reference and their whole queue was
 * replaced by the dashboard with no explanation, which in a demonstration reads
 * as a crash. Where there is no detail page the reference goes to the list the
 * record sits on instead.
 *
 * A kind missing from this map has no screen that shows it — a direct debit
 * mandate is approved here but is not displayed anywhere yet — so its reference
 * stays as plain text rather than becoming a link that goes nowhere.
 */
const SUBJECT_ROUTES: Record<string, { list: string; detail?: string }> = {
  purchase_requisition: { list: '/requisitions', detail: '/requisitions' },
  purchase_invoice: { list: '/invoices', detail: '/invoices' },
  payment_batch: { list: '/payments', detail: '/payments' },
  journal: { list: '/ledger', detail: '/ledger/journals' },
  sales_invoice_request: { list: '/sales' },
  supplier_request: { list: '/self-service' },
  expense_claim: { list: '/self-service' },
};

function routeFor(item: { subjectType: string; subjectId: string }): string | null {
  const route = SUBJECT_ROUTES[item.subjectType];
  if (!route) return null;
  return route.detail ? `${route.detail}/${item.subjectId}` : route.list;
}

/**
 * The name of a document kind, in the tenant's own words.
 *
 * Not `statusLabel`, which maps document STATES; a subject type run through it
 * comes out as whatever sentence-casing the enum happens to produce, so a
 * tenant who calls these "Purchase requests" would still read "Purchase
 * requisition" on this one screen.
 */
function subjectLabel(type: string, t: Terminology): string {
  const labels: Record<string, string> = {
    purchase_requisition: t.requisition,
    purchase_invoice: `${t.supplier} invoice`,
    payment_batch: 'Payment run',
    journal: 'Journal',
    sales_invoice_request: 'Sales invoice request',
    supplier_request: `New ${lower(t.supplier)} request`,
    supplier_bank_change: `${t.supplier} bank detail change`,
    expense_claim: 'Expense claim',
    direct_debit_mandate: 'Direct debit mandate',
    budget_transfer: 'Budget transfer',
    procurement_card_statement: 'Procurement card statement',
    depreciation_run: 'Depreciation run',
  };
  return labels[type] ?? humanise(type);
}

/**
 * `todayIso` gives today's date in London; this gives any instant's date in
 * London, which is what turns a due timestamp into a day the reader recognises.
 */
const LONDON_DAY = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/London',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/**
 * Whole days between today and a due date, counted in calendar days rather than
 * in elapsed hours. Dividing the gap by twenty-four would make something due at
 * nine tomorrow morning read as due today for most of this afternoon, and "due
 * tomorrow" has to mean tomorrow's date to be worth printing.
 */
function daysUntil(iso: string): number | null {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;
  const due = Date.parse(`${LONDON_DAY.format(at)}T00:00:00Z`);
  const today = Date.parse(`${todayIso()}T00:00:00Z`);
  if (Number.isNaN(due) || Number.isNaN(today)) return null;
  return Math.round((due - today) / 86_400_000);
}

/**
 * How a due date reads on an approval SLA.
 *
 * `relativeTime` cannot be used here. It measures time ELAPSED, so every future
 * instant lands in its "just now" branch — an item due tomorrow was telling the
 * Finance Director it was due just now, on the one column that is supposed to
 * show whether the workflow is running to time.
 */
function dueLabel(iso: string): string {
  const days = daysUntil(iso);
  if (days === null) return date(iso);
  if (days === 0) return 'Today';
  if (days === 1) return 'Tomorrow';
  if (days > 1 && days <= 14) return `In ${days} days`;
  return date(iso);
}

export function ApprovalsPage() {
  const t = useTerms();
  const queue = useAsync(() => api.get<{ approvals: PendingApproval[] }>('/procurement/approvals/pending'), []);
  const [deciding, setDeciding] = useState<{ item: PendingApproval; decision: 'approve' | 'reject' } | null>(null);
  const [comment, setComment] = useState('');
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  const decide = useMutation(async (item: PendingApproval, decision: 'approve' | 'reject', text: string) =>
    api.post<DecisionResponse>('/procurement/approvals/decide', {
      subjectType: item.subjectType,
      subjectId: item.subjectId,
      decision,
      comment: text.trim() || undefined,
    }),
  );

  const beginDecision = (item: PendingApproval, decision: 'approve' | 'reject') => {
    // Clear the previous confirmation as the next decision begins, so the
    // banner on screen always describes the decision just made.
    setOutcome(null);
    decide.clearError();
    setComment('');
    setDeciding({ item, decision });
  };

  const close = () => {
    setDeciding(null);
    decide.clearError();
  };

  const submit = async () => {
    if (!deciding) return;
    const result = await decide.run(deciding.item, deciding.decision, comment);
    if (result) {
      setOutcome({
        decision: deciding.decision,
        reference: deciding.item.subjectReference,
        subjectType: deciding.item.subjectType,
        subjectId: deciding.item.subjectId,
        originatorName: deciding.item.originatorName,
        result,
      });
      setDeciding(null);
      setComment('');
      queue.reload();
    }
  };

  return (
    <>
      <PageHeader
        title="My approvals"
        subtitle={`Everything waiting for a decision from you, in this ${lower(t.entity)}.`}
      />

      {outcome ? <DecisionConfirmation outcome={outcome} terms={t} /> : null}

      <AsyncPanel state={queue}>
        {(data) => {
          const items = data.approvals;
          // Only total when there is one currency to total. Adding euros to
          // pounds and putting a £ in front of the answer is the kind of figure
          // a finance director spots from across the room.
          const currencies = new Set(items.map((a) => a.currency));
          const total = currencies.size === 1 ? money(items.reduce((s, a) => s + a.valueMinor, 0)) : null;

          return (
            <Card
              title={`${items.length} item${items.length === 1 ? '' : 's'} awaiting you`}
              subtitle={
                items.length === 0
                  ? undefined
                  : total
                    ? `Total value ${total}`
                    : 'Several currencies — each item carries its own value'
              }
              flush
            >
              {items.length === 0 ? (
                <div style={{ padding: '1.5rem' }}>
                  <EmptyState title="Nothing is waiting for you" icon="✓">
                    When something needs your approval it appears here, and you are reminded before it becomes overdue.
                  </EmptyState>
                </div>
              ) : (
                <DataTable
                  caption="Items awaiting your approval"
                  rows={items}
                  rowKey={(r) => r.requestId}
                  // A director's queue can run to dozens of items in a busy
                  // week; a queue of two does not need a search box above it.
                  searchValue={
                    items.length > 8
                      ? (r: PendingApproval) =>
                          `${r.subjectReference} ${r.subjectSummary} ${r.originatorName} ${r.costCentreCode ?? ''} ${subjectLabel(r.subjectType, t)}`
                      : undefined
                  }
                  searchLabel="Search these approvals"
                  pageSize={items.length > 25 ? 25 : undefined}
                  columns={[
                    {
                      key: 'ref',
                      header: 'Reference',
                      width: '11rem',
                      sortValue: (r) => r.subjectReference,
                      render: (r) => {
                        const to = routeFor(r);
                        return to ? (
                          <Link to={to}>
                            <span className="mono">{r.subjectReference}</span>
                          </Link>
                        ) : (
                          <span className="mono">{r.subjectReference}</span>
                        );
                      },
                    },
                    {
                      key: 'what',
                      header: 'What is being asked',
                      sortValue: (r) => r.subjectSummary,
                      render: (r) => (
                        <>
                          <div>{r.subjectSummary}</div>
                          <div className="smaller muted">
                            {subjectLabel(r.subjectType, t)} · raised by {r.originatorName}
                            {r.costCentreCode ? ` · ${t.costCentre} ${r.costCentreCode}` : ''}
                          </div>
                          {r.onBehalfOfName ? (
                            <div className="smaller">
                              <Badge tone="info">Delegated from {r.onBehalfOfName}</Badge>
                            </div>
                          ) : null}
                        </>
                      ),
                    },
                    {
                      key: 'stage',
                      header: 'Stage',
                      sortValue: (r) => r.stageNo,
                      render: (r) => (
                        <>
                          <div className="smaller">{r.stageName}</div>
                          <div className="smaller muted">Stage {r.stageNo}</div>
                        </>
                      ),
                    },
                    {
                      key: 'value',
                      header: 'Value',
                      numeric: true,
                      sortValue: (r) => r.valueMinor,
                      render: (r) => <Money minor={r.valueMinor} />,
                    },
                    {
                      key: 'due',
                      header: 'Due',
                      // Nulls sort last either way, which puts the items with a
                      // deadline at the top when the column is pointed at the
                      // soonest first.
                      sortValue: (r) => r.dueAt,
                      render: (r) =>
                        r.isOverdue ? (
                          <>
                            <Badge tone="error">Overdue</Badge>
                            {r.dueAt ? <div className="smaller muted">{date(r.dueAt)}</div> : null}
                          </>
                        ) : r.dueAt ? (
                          <>
                            <div className="smaller" title={dateTime(r.dueAt)}>
                              {dueLabel(r.dueAt)}
                            </div>
                            <div className="smaller muted">{date(r.dueAt)}</div>
                          </>
                        ) : (
                          <span className="smaller muted">No deadline</span>
                        ),
                    },
                    {
                      key: 'actions',
                      header: <span className="visually-hidden">Decision</span>,
                      render: (r) => (
                        <div className="btn-group">
                          <button type="button" className="btn btn--sm btn--primary" onClick={() => beginDecision(r, 'approve')}>
                            Approve
                          </button>
                          <button type="button" className="btn btn--sm btn--danger" onClick={() => beginDecision(r, 'reject')}>
                            Reject
                          </button>
                        </div>
                      ),
                    },
                  ]}
                />
              )}
            </Card>
          );
        }}
      </AsyncPanel>

      <Modal
        open={deciding !== null}
        onClose={close}
        title={deciding?.decision === 'reject' ? 'Reject this request' : 'Approve this request'}
        footer={
          <>
            <button type="button" className="btn" onClick={close} disabled={decide.busy}>
              Cancel
            </button>
            <button
              type="button"
              className={deciding?.decision === 'reject' ? 'btn btn--danger' : 'btn btn--primary'}
              onClick={() => void submit()}
              disabled={decide.busy || (deciding?.decision === 'reject' && comment.trim().length === 0)}
            >
              {decide.busy ? 'Recording…' : deciding?.decision === 'reject' ? 'Reject' : 'Approve'}
            </button>
          </>
        }
      >
        {deciding ? (
          <div className="stack">
            <p>
              <span className="mono">{deciding.item.subjectReference}</span> — {deciding.item.subjectSummary}
            </p>
            <p className="muted">
              {money(deciding.item.valueMinor)} · {subjectLabel(deciding.item.subjectType, t)} · raised by{' '}
              {deciding.item.originatorName} · {deciding.item.stageName}
            </p>
            <TextArea
              label={deciding.decision === 'reject' ? 'Why are you rejecting this?' : 'Comment (optional)'}
              hint={
                deciding.decision === 'reject'
                  ? 'The requester sees this. A rejection without a reason gives them nothing to act on.'
                  : 'Recorded against your decision in the audit trail.'
              }
              value={comment}
              onChange={(e) => setComment(e.currentTarget.value)}
              required={deciding.decision === 'reject'}
            />
            <ErrorNotice error={decide.error} />
          </div>
        ) : null}
      </Modal>
    </>
  );
}

/**
 * What the decision did.
 *
 * Every sentence here comes from the server's own answer — whether the request
 * completed, which stage it moved to and who now holds it. Nothing is inferred,
 * because the one thing worse than telling the approver nothing is telling them
 * something that turns out not to be what happened.
 */
function DecisionConfirmation({ outcome, terms }: { outcome: Outcome; terms: Terminology }) {
  const { result } = outcome;
  const to = routeFor(outcome);
  const openRecord = to ? (
    <>
      {' '}
      <Link to={to}>Open {outcome.reference}</Link>.
    </>
  ) : null;

  if (outcome.decision === 'reject' || result.requestStatus === 'rejected') {
    return (
      <Alert tone="warn" title={`${outcome.reference} was not approved.`}>
        {outcome.originatorName} has been told, with the reason you gave, and your decision is on the audit trail.
        {openRecord}
      </Alert>
    );
  }

  if (result.requestStatus === 'in_progress') {
    return (
      <Alert tone="ok" title={`${outcome.reference} approved at stage ${result.completedStageNo}.`}>
        {result.nextAssigneeName
          ? `It has passed to ${result.nextAssigneeName} for stage ${result.nextStageNo ?? result.completedStageNo + 1}, and has left your queue.`
          : `It has passed to stage ${result.nextStageNo ?? result.completedStageNo + 1} and has left your queue.`}
        {openRecord}
      </Alert>
    );
  }

  return (
    <Alert tone="ok" title={`${outcome.reference} is fully approved.`}>
      {result.fulfilmentMethod === 'purchase_order'
        ? `Yours was the authorising decision. It is now ready for a ${lower(terms.purchaseOrder)} to be raised.`
        : 'Yours was the authorising decision, and it is recorded against your name and your authority at the time.'}
      {openRecord}
    </Alert>
  );
}
